export interface LiveOutstandingValueResponse {
  outStandingAmt: number;
}

// GET /sales/live/v2/by-channel — one row per channel.
// Shares a response shape with /sales/live/v2/staff-perfomance, but only the fields that make
// sense for a per-channel rollup are populated: `channel` is real and `staff_name` is always
// null (the mirror image of staff-perfomance). Anything the endpoint does not compute comes
// back null rather than being omitted.
export interface LiveByChannelItem {
  orderCount: number;
  netSales: number;
  tip: number;
  tax: number | null;
  discount: number;
  grandTotal: number;
  gratuity: number;
  tipAndGratuity: number | null;
  ordersWithTips: number | null;
  ordersWithGratuity: number | null;
  otherCharges: number | null;
  outStandingAmt: number;
  channel: string;
  /**
   * The channel's source: `'direct'` for the outlet's own orders, otherwise the marketplace
   * platform's own name (`'Grubhub'`, `'Zomato'`, `'GloriaFood'`, …) — NOT the literal
   * `'thirdparty'` the `orderSource` QUERY param takes. Anything that is not `'direct'` is
   * third party, which is how the EOD Channel Revenue card splits its two series.
   */
  orderSource: string | null;
  timezoneCode: string | null;
  /** Always null on this endpoint — use staff-perfomance for anything staff-attributed. */
  staff_name: string | null;
  refund: number | null;
  todayAvgOrder: number | null;
  guestCount: number | null;
  spendPerGuest: number | null;
  feeRefund?: number | null;
  /**
   * ZAR-2371 — % change in net sales vs the comparison window (compStartDate/compEndDate sent
   * by the FE). Null/absent when the row has no comparable prior data → the card shows "N/A".
   * grandTotalDelta / orderCountDelta / todayAvgOrderDelta also come back but are not displayed.
   */
  netSalesDelta?: number | null;
  grandTotalDelta?: number | null;
  orderCountDelta?: number | null;
  todayAvgOrderDelta?: number | null;
}

/** ZAR-2371 — `type` on /sales/live/v2/by-channel. Omitted = channel rows; 'source' = order-source rows. */
export type ByChannelType = 'source';

export interface LiveItemSalesV2Item {
  itemName: string;
  categoryName: string | null;
  quantity: number;
  price: number;
}

export interface SnapshotDetailsResponse {
  orderCount: number;
  netSales: number;
  tip: number;
  tax: number | null;
  discount: number;
  guestCount: number;
  grandTotal: number;
  gratuity: number;
  // ZAR-1762 — the unified Tips & Gratuity KPI reads these three directly instead of
  // deriving them from the staff-performance buckets:
  //   tipAndGratuity     — combined total shown on the card face
  //   ordersWithTips     — count of tip-bearing orders (drill-in, Tips section)
  //   ordersWithGratuity — count of gratuity-bearing orders; the base Avg/Gratuity Order divides by
  tipAndGratuity: number;
  ordersWithTips: number;
  ordersWithGratuity: number;
  otherCharges?: number;
  outStandingAmt?: number;
  spendPerGuest?: number;
  // Service charge — every outlet reports it; absent/0 simply means none was collected.
  // The backend already applies `serviceChargeRetained` to `netSales`, `tax` and
  // `grandTotal`: when retained they include the charge (and its tax); when not retained
  // only `grandTotal` does. `serviceFee` is the amount collected, pre-tax.
  serviceChargeRetained?: boolean;
  serviceFee?: number;
  // REF-1 — item vs fee refund components, for the Sales Waterfall's Refunds/Others rows.
  // `refund` reduces Net Sales (item component); `feeRefund` nets out of Others (convenience
  // + delivery fees). Tax and tips/gratuity refund components are not split out here yet.
  refund?: number;
  feeRefund?: number;
  channel: null;
  timezoneCode: null;
}

export interface LivePaymentsItem {
  tenderType: string;
  tenderAmount: number;
  transactionCount: number;
}

export interface LiveRefundSummaryItem {
  cancelReason: string;
  removedQty: number;
  orderCount: number;
  cancelledAmt: number;
}

export interface LiveVoidSummaryItem {
  cancelReason: string;
  removedQty: number;
  orderCount: number;
  cancelledAmt: number;
  // Percent change vs. the comparison period, for the Voided Items stamp KPI's delta.
  // Repeated identically across every reason row (it's a period-level figure, not
  // per-reason), so any row's value is read for the tile.
  delta?: number;
  // Distinct orders with a void across ALL reasons — period-level, repeated on every row.
  // Use this for the order count; summing orderCount counts an order once per reason.
  totalOrders?: number;
}

// GET /void-staff — Voids by Staff ranked bars' real data source.
export interface LiveVoidByStaffItem {
  staffName: string;
  removedQty: number;
  orderCount: number;
  cancelledAmt: number;
}

// GET /void-kpis — the Void drill-in's Mini-KPIs row (Voided Items · Void Value · Void Rate ·
// Avg Void Value). `voidRate` arrives pre-computed (voidedItems ÷ itemsSold × 100, 1dp).
export interface LiveVoidKpisResponse {
  voidedItems: number;
  voidValue: number;
  voidRate: number;
  avgVoidValue: number;
  itemsSold: number;
}

export interface LiveRefundDetailsItem {
  orderNo: string;
  orderDate?: string;
  voidedTime: string;
  staffName: string;
  cancelReason: string;
  cancelledAmt: number;
  itemName: string | null;
  channel: string;
}

/**
 * REF-2 — GET /sales/live/v2/refund-log. The real, richer-than-refund-details endpoint this
 * epic was built to consume: `refundType` and `reason` are real, and money is broken into
 * item / tax / tips-and-gratuity (combined) rather than one flat `cancelledAmt`.
 *
 * Still flatter than the FE's own `RefundRecord` model — no per-tender breakdown, no
 * separate tip vs gratuity, no distinct convenience/delivery fee, no channel-group or
 * tip-out-exposure flag. `refundLogRow.ts` maps this onto `RefundRecord` as faithfully as
 * the fields allow; see that file for exactly which parts are approximated.
 */
export interface RefundLogItem {
  /** The order's id — what GET /sales/live/v2/order-detail takes. Absent on older backends. */
  orderId?: string | null;
  orderNo: string;
  orderDate: string;
  /** The date the refund POSTED — may differ from `orderDate`. */
  refundDate: string;
  refundType: 'Full' | 'Item' | 'Fee' | string;
  reason: string;
  /** Empty string, not null, when the API has no staff on the row. */
  staffName: string;
  channel: string;
  itemsRefunded: number | null;
  taxRefunded: number | null;
  /** Tips AND gratuity combined — the API does not split them. */
  tipsAndGratRefunded: number | null;
  convenienceFeeRefunded: number | null;
  deliveryRefunded: number | null;
  serviceChargeRefunded: number | null;
  cardProcessingFeeRefunded: number | null;
  /** Total cash returned for this order. */
  refunded: number;
}

export interface RefundLogResponse {
  content: RefundLogItem[];
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
  /** Period-level aggregates, straight from the API — used for the context strip. */
  totalRefunds?: number;
  totalRefundedAmt?: number;
  totalItemsAmt?: number;
  totalFeesAmt?: number;
  /** Aggregates for the current filtered/searched result set — the Refund Log table's own
   * Convenience Fee / Delivery Fee / Service Charge totals row reads these directly rather
   * than summing the page's own rows. */
  totalConvenienceFeeAmt?: number;
  totalDeliveryAmt?: number;
  totalServiceChargeAmt?: number;
  totalCardProcessingFeeAmt?: number;
}

/** REF-2 — GET /sales/live/v2/refund-log-filters. Not yet deployed. */
export type RefundLogFilterKind = 'type' | 'reason' | 'staff' | 'channel';

export interface RefundLogFiltersResponse {
  filterType: string;
  /** 'type' → fixed ['Full','Item','Fee']. 'reason'/'staff'/'channel' → whatever actually
   * occurred in the requested date range (blank reason → 'No Reason', blank staff → 'System'). */
  values: string[];
}

/**
 * REF-6 — GET /sales/live/v2/refund-reasons. Pre-segmented at the source (separate
 * `itemReasons` / `feeReasons` sections), so the mapper doesn't need `reasonGroupOf()`'s
 * text-based classification the way `refund-log` rows do.
 */
export interface RefundReasonApiRow {
  reason: string;
  occurrenceCount: number;
  totalAmount: number;
}

/**
 * One paginated section of the refund-reasons response. `itemReasons` (Full + Item refunds)
 * and `feeReasons` (everything else) each carry their OWN totals — they receive the same
 * `page`/`size` but their `totalPages` are computed separately, so never assume they move
 * together. Each is sorted by `totalAmount` desc.
 */
export interface RefundReasonPage {
  content: RefundReasonApiRow[];
  totalElements: number;
  totalPages: number;
}

/** Query params both paginated REF-6 endpoints take (`page` is 1-indexed, `size` defaults to 10). */
export interface RefundPageParams {
  page?: number;
  size?: number;
}

export interface RefundReasonsResponse {
  page: number;
  size: number;
  itemReasons: RefundReasonPage;
  feeReasons: RefundReasonPage;
}

/** REF-6 — one row of GET /sales/live/v2/refund-by-staff. */
export interface RefundByStaffApiRow {
  /** Empty string, not null, when the API has no staff on the row. */
  staffName: string;
  itemCount: number;
  feeCount: number;
  totalRefunded: number;
}

/**
 * REF-6 — GET /sales/live/v2/refund-by-staff: ONE flat page (unlike refund-reasons, which is
 * split in two), sorted by `totalRefunded` desc. `page` is 1-indexed; a page past `totalPages`
 * comes back with an empty `content`, not an error.
 */
export interface RefundByStaffResponse {
  content: RefundByStaffApiRow[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * REF-1 — GET /sales/live/v2/refund-component-split. `component` is one of 'Item' | 'Tax' |
 * 'Tip' | 'Gratuity' | 'Convenience Fee' | 'Delivery Fee'; `feeComponent` is false only for
 * 'Item' — everything else is cash reconciliation / posts to Others, never Net Sales.
 */
export interface RefundComponentSplitApiRow {
  component: string;
  amount: number;
  feeComponent: boolean;
}

export interface RefundComponentSplitResponse {
  components: RefundComponentSplitApiRow[];
  total: number;
  emptyState: boolean;
  /** Backend disclaimer string — the card renders its own equivalent copy, computed from the real amounts, rather than this verbatim. */
  note?: string;
}

export interface LiveVoidDetailsItem {
  orderNo: string;
  orderDate?: string;
  voidedTime: string;
  staffName: string;
  cancelReason: string;
  cancelledAmt: number;
  itemName: string;
  voidedQty?: number;
}

export interface LiveVoidDetailsResponse {
  content: LiveVoidDetailsItem[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
  size?: number;
}

export interface LiveRefundDetailsResponse {
  content: LiveRefundDetailsItem[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
  size?: number;
}

export interface LiveHourlySalesItem {
  hour: string;
  grandTotal: number;
  itemTotal: number;
  orderCount: number;
}

// Response item for GET /sales/live/v2/hourly-sales?locationId=&startDate=&endDate=
export interface HourlySalesItem {
  hour: string;
  hourValue: number;
  grandTotal: number;
  itemTotal: number;
  orderCount: number;
}

export interface HourlySalesResponse {
  hours: HourlySalesItem[];
  operatingStartHour: number;
  operatingEndHour: number;
  operatingHourCount: number;
  closed: boolean;
}

// Response item for GET /sales/live/v2/staff-perfomance (Today only)
// Drives the Tips leaderboard and the Total Discounts stamp KPI drilldown on SalesScreen.
// Discounts drilldown KPIs: sum(discount) = Total Discounts, sum(orderCount) = Disc. Orders,
// sum(netSales)/sum(discount) = Effectiveness, top discount holder = Top Discounter.
export interface LiveStaffPerformanceItem {
  orderCount: number;
  netSales: number;
  tip: number;
  tax: number;
  discount: number;
  grandTotal: number;
  gratuity: number;
  tipAndGratuity: number | null;
  ordersWithTips: number | null;
  ordersWithGratuity: number | null;
  otherCharges: number | null;
  outStandingAmt: number;
  /** Always null on this endpoint — use by-channel for anything channel-attributed. */
  channel: string | null;
  timezoneCode: string | null;
  staff_name: string;
  refund: number | null;
  todayAvgOrder: number | null;
  guestCount: number | null;
  spendPerGuest: number | null;
}

// Response item for GET /sales/live/v2/staff-active-clocking
// Drives the Active Staff stamp KPI on the Snapshot screen for Today.
export interface LiveStaffActiveClockingItem {
  staffName: string;
  grandTotal: number;
  netSales: number;
  lastOrderDate: string | null;
  firstClockIn: string | null;
  totalHours: string | null;
  currentStatus: string;
}

// Response item for GET /sales/live/v2/staff-active-clocking-v2
// Drives the Staff Roster drilldown (paginated/searchable/sortable, any date range)
// under the Active Staff stamp KPI on the Snapshot screen.
export interface LiveStaffActiveClockingV2Item {
  date: string | null;
  employeeName: string;
  clockIn: string | null;
  clockOut: string | null;
  workedHours: string | null;
  breakHours: string | null;
  status: string | null;
  grandTotal: number;
  netSales: number;
}

// Response for GET /sales/live/v2/staff-active-clocking-v2
export interface LiveStaffActiveClockingV2Response {
  content: LiveStaffActiveClockingV2Item[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  staffWorked: number;
  activeCount: number;
  breakCount: number;
  totalWorkedHours: number;
  totalNetSales: number;
  avgSalesPerStaff: number;
}

export interface RemovedItemModifier {
  name: string;
  price: number;
  /** Per-modifier quantity — optional since removed items may not carry it; defaults to 1. */
  quantity?: number;
  /** price × quantity — optional since removed items may not carry it; falls back to `price`. */
  totalPrice?: number;
}

// Response for GET /sales/live/v2/order-detail
export interface OrderDetailItem {
  itemName: string;
  /** Array of {name, price} — same shape as a removed item's modifiers, not a joined string. */
  modifiers: RemovedItemModifier[];
  qty: number;
  unitPrice: number;
  lineTotal: number;
  /** The item's own per-unit price, excluding modifiers — shown in the Items table's Price column. */
  itemUnitPrice: number;
  offerName?: string;
}

export interface RemovedItem {
  item: string;
  modifiers: RemovedItemModifier[];
  quantity: number;
  unitPrice?: number;
  total: number;
  cancelReason: string;
  offerName?: string;
}

export interface OrderOfferItem {
  offerName: string;
  offerDiscount: number;
}

export interface OrderLoyaltyInfo {
  loyaltypoints: string;
  loyaltyValue: number;
}

/** REF-5 — one refunded component, as GET /sales/live/v2/order-detail names it. */
export interface OrderRefundComponent {
  /** e.g. 'Item', 'Tax', 'Tips', 'Gratuity', 'Delivery Fee', 'Convenience Fee'. */
  component: string;
  amount: number;
}

/** REF-5 — one tender's share of the refund. */
export interface OrderRefundTender {
  tenderType: string;
  last4digit?: string | null;
  cardType?: string | null;
  amount: number;
}

/** One distinct refund action on the order (a row in Refund History). */
export interface OrderRefundDetail {
  /** Store local wall-clock time, e.g. '2026-09-21 18:47:49' — never re-zoned. */
  refundedAt: string;
  /** 'System' when unattended. */
  staffName: string;
  /** 'Full' (whole order) | 'Item' | 'Fee' (anything else — tax, tip, service charge…). */
  refundType: 'Full' | 'Item' | 'Fee' | string;
  reason: string;
}

/**
 * REF-5 — the order's refund history.
 *
 * Legacy shape: ONE event, flat (`refundedAt`/`staffName`/`refundType`/`reason`) with
 * `components`/`tenders`/`refundTotal` nested inside.
 * New shape: `refundDetails[]` (one entry per refund action — never dedupe, two 'Fee'
 * rows with the same reason are two different charges); `components`/`tenders`/`refundTotal`
 * are then sent at the top level of the response (see `OrderDetailResponse`).
 * The reader accepts both so it works before and after the BE deploy.
 */
export interface OrderRefundHistory {
  refundDetails?: OrderRefundDetail[];
  /** Legacy single-event fields. */
  refundedAt?: string;
  /** Empty string, not null, when the API has no staff on the event. */
  staffName?: string;
  refundType?: 'Full' | 'Item' | 'Fee' | string;
  reason?: string;
  components?: OrderRefundComponent[];
  tenders?: OrderRefundTender[];
  refundTotal?: number;
}

export interface OrderDetailResponse {
  orderId: string;
  orderType: string;
  orderTime: string;
  orderStatus: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  guests?: number;
  tableName?: string | null;
  deliveryAddress: string | null;
  items: OrderDetailItem[];
  subtotal: number;
  discounts: number;
  /** Every discount on the order (order-level + item-level offers) — feeds the Financial Breakdown's Discount line. */
  totalDiscount?: number;
  offers?: OrderOfferItem[];
  loyalty?: OrderLoyaltyInfo | null;
  tax: number;
  tip: number;
  total: number;
  gratuity?: number;
  deliveryCharges?: number;
  convenienceFee?: number;
  cardProcessingFee?: number;
  /** Total amount refunded on this order — its own line in the Financial Breakdown. */
  refundAmount?: number | null;
  /** Absent/null for outlets that do not capture a service charge — the line is then hidden. */
  serviceCharge?: number | null;
  staffName: string;
  removedItems?: RemovedItem[];
  /** REF-5 — 'Refunded' (full) | 'Partially Refunded' (item or fee) | absent when unrefunded. */
  refundedOrderStatus?: string | null;
  refundHistory?: OrderRefundHistory | null;
  /** New BE shape: refund components/tenders/total live at the top level (legacy: inside `refundHistory`). */
  components?: OrderRefundComponent[];
  tenders?: OrderRefundTender[];
  refundTotal?: number;
}

// Response item for GET /sales/live/v2/order-log
export interface OrderLogItem {
  orderNo: string;
  orderId: string;
  orderDate?: string;
  orderTime: string;
  orderType: string;
  customerName: string;
  /** Em dash when the order carries no phone */
  customerPhone: string;
  guests?: number;
  itemPreview: string;
  /** Grand Total — what the guest actually paid */
  amount: number;
  /** Every table the check was closed on; empty for non-dine-in (ORD-1.2) */
  tableName?: string[];
  staffName?: string | null;
  /** Cancellation reason, returned only by the `type=cancel` log (ORD-1.3) */
  reason?: string;
  // ── Financial waterfall (ORD-1.4). ──
  itemTotal?: number;
  discount?: number;
  /** Every discount on the order (order-level + item-level offers); supersedes `discount`. */
  totalDiscount?: number;
  serviceCharge?: number | null;
  deliveryFee?: number;
  convenienceFee?: number;
  tax?: number;
  tips?: number;
  gratuity?: number;
  processingFee?: number;
  /**
   * REF-5 — real refund fields. `refundType` is 'Full' | 'Item' | 'Fee' (case as sent by
   * the API); absent when the order carries no refund. `refunded` is the total cash
   * returned across all components for this order.
   */
  refundType?: string | null;
  refunded?: number | null;
  updated_time?: string;
}

export interface OrderLogResponse {
  orders: OrderLogItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  /** Segment counts for the period — both are returned by BOTH logs (ORD-1.3) */
  paidCount?: number;
  cancelledCount?: number;
  /** False when the outlet runs no table service — hides Table No. (ORD-1.2) */
  isDineInAvailable?: boolean;
}

// Response item for GET /sales/live/v2/unpaid-orders (Today only)
export interface UnpaidOrderItem {
  orderNo: string;
  orderId: string;
  orderDate?: string;
  orderTime: string;
  serverName: string;
  orderType: string;
  itemPreview?: string;
  amount: number;
  customerName?: string;
  guestCount?: number;
  /** Every table the check is open on; `[]` for non-dine-in (ORD-1.2) */
  tableName?: string[] | string | null;
  // ── Financial waterfall (ORD-1.4). The unpaid endpoint does not return these
  // today — each stays undefined and the column renders an em dash. Declared so
  // the columns populate on their own once the API starts supplying them, the
  // way `tableName` silently changed shape underneath us.
  itemTotal?: number;
  discount?: number;
  deliveryFee?: number;
  convenienceFee?: number;
  tax?: number;
  tips?: number;
  gratuity?: number;
  processingFee?: number;
  /** REF-5 — same real refund fields as `OrderLogItem`; see its own doc comment. */
  refundType?: string | null;
  refunded?: number | null;
}

export interface UnpaidOrdersResponse {
  orders: UnpaidOrderItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  /** False when the outlet runs no table service — hides Table No. (ORD-1.2) */
  isDineInAvailable?: boolean;
}

// ── Complimentary Orders ─────────────────────────────────────────────────────

// GET /sales/live/v2/comp-order-log
export interface CompOrderLogItem {
  orderNo: string;
  orderId: string;
  orderTime: string;
  staffName: string;
  cancelReason: string;
  items: string[];
  amount: number;
}

export interface CompOrderLogResponse {
  orders: CompOrderLogItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

// GET /sales/live/v2/comp-reasons
export interface CompReasonItem {
  cancelReason: string;
  occurrenceCount: number;
  totalAmount: number;
}

export interface CompReasonsResponse {
  reasons: CompReasonItem[];
}

// GET /sales/live/v2/comp-by-staff
export interface CompByStaffItem {
  staffName: string;
  compCount: number;
  totalCompValue: number;
}

export interface CompByStaffResponse {
  staff: CompByStaffItem[];
}

// Response item for GET /sales/live/v2/categoy-sales (Today only)
export interface LiveCategorySalesItem {
  itemName: string | null;
  categoryName: string;
  quantity: number | null;
  price: number;
}

// Paginated response for GET /sales/live/v2/categoy-sales (page/size driven).
// `page` is 1-indexed (matches the Order Log convention). Older deployments may
// still return a bare LiveCategorySalesItem[] — the saga tolerates both.
export interface LiveCategorySalesResponse {
  categories: LiveCategorySalesItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

// Response item for GET /sales/live/v2/staff-refunds (Today only)
// Drives the Refunds by Staff panel + Top Refunder stamp KPI on the SalesScreen refunds drilldown.
export interface LiveStaffRefundsItem {
  orderCount: number;
  netSales: number | null;
  tip: number | null;
  tax: number | null;
  discount: number | null;
  grandTotal: number | null;
  gratuity: number | null;
  outStandingAmt: number | null;
  channel: string | null;
  timezoneCode: string | null;
  staff_name: string;
  refund: number;
  todayAvgOrder: number | null;
}

// Response for GET /sales/live/v2/snapshot-details/comparison (Today only)
// All numeric fields are percentage-change values vs the FE-derived comparison window.
//
// `currentTime` / `locationTimezone` are the server's own clock reading at the moment it
// computed those numbers — the wall clock at the STORE, in `locationTimezone`. They drive
// the "(to 7 AM)" truncation label on the date control (ZAR-1662): the label then states
// where the DATA stops, not where the viewer's device clock happens to stand. Both are
// optional so an older backend that omits them simply falls back to the browser clock.
export interface SnapshotComparisonResponse {
  orderCount: number;
  netSales: number;
  tip: number;
  tax: number;
  discount: number;
  grandTotal: number;
  gratuity: number;
  // ZAR-1762 / TG-1 — percent change on the combined tips + gratuity total. Backend-computed,
  // so the unified card never has to back-solve a combined delta from the two separate ones.
  tipAndGratuity: number;
  outStandingAmt: number;
  refund: number;
  avgOrderValue: number;
  unpaidOrder: number;
  compleOrder: number;
  guestCount: number;
  spendPerGuest?: number;
  /** Server clock at the store, "YYYY-MM-DD HH:mm:ss" in `locationTimezone`. */
  currentTime?: string;
  /** IANA zone the reading above is expressed in, e.g. "America/Chicago". */
  locationTimezone?: string;
  /** CANC-1 — percent change on the cancelled-orders count vs the comparison period. */
  cancelledOrders: number;
  /** Percent change on service charge. null when the prior period had none (card shows "—"). */
  serviceFee?: number | null;
}

export interface ClickHouseData {
  id: string;
  locationId: string;
  [key: string]: unknown;
}

export interface ClickHouseState {
  data: ClickHouseData | null;
  loading: boolean;
  error: string | null;
  snapshotDetails: SnapshotDetailsResponse | null;
  snapshotDetailsLoading: boolean;
  snapshotDetailsError: string | null;
}

// GET /sales/live/v2/labour-employee-sales
export interface LabourEmployeeSalesItem {
  employeeName: string;
  sales: number;
  orderCount: number;
  tips: number;
}

export interface LabourEmployeeSalesSummary {
  totalSales: number;
  avgSalesPerEmployee: number;
  topSeller: string;
  splh: string;
  totalTips: number | null;
  avgTipsPerEmployee: number | null;
  tipRate: number | null;
}

export interface LabourEmployeeSalesResponse {
  content: LabourEmployeeSalesItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  summary: LabourEmployeeSalesSummary;
}

// GET /sales/live/v2/labour-voids
export interface LabourVoidsReason {
  cancelReason: string;
  removedQty: number | null;
  orderCount: number;
  cancelledAmt: number | null;
}

export interface LabourVoidsByEmployee {
  employeeName: string;
  voidCount: number;
  voidAmount: number;
}

export interface LabourVoidsResponse {
  totalVoids: number;
  totalVoidAmount: number;
  avgVoidValue: number;
  reasons: LabourVoidsReason[];
  byEmployee: LabourVoidsByEmployee[];
}

// GET /sales/live/v2/labour-tips
export interface LabourTipsItem {
  employeeName: string;
  tips: number;
  tipRate: number;
}

export interface LabourTipsSummary {
  totalSales: number | null;
  avgSalesPerEmployee: number | null;
  topSeller: string | null;
  splh: string | null;
  totalTips: number;
  avgTipsPerEmployee: number;
  tipRate: number;
}

export interface LabourTipsResponse {
  content: LabourTipsItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  summary: LabourTipsSummary;
}

// GET /sales/live/v2/labour-working-hours
export interface LabourWorkingHoursItem {
  employeeName: string;
  workingHours: string;  // "Xh Ym" pre-formatted
  breakHours: string;    // "Xh Ym" pre-formatted
}

export interface LabourWorkingHoursSummary {
  totalBreakHours: string;
  netProductiveHours: string;
  avgHoursPerEmployee: string;
}

export interface LabourWorkingHoursResponse {
  content: LabourWorkingHoursItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  summary: LabourWorkingHoursSummary;
}

// GET /sales/live/v2/labour-staff-performance
export interface LabourStaffPerformanceItem {
  employeeName: string;
  totalHours: string;    // "Xh Ym" formatted string
  breakHours: string;    // "Xh Ym" formatted string
  sales: number;
  splh: number | null;
  tips: number;
  gratuity: number;
  voids: number;
  voidAmount: number;
}

export interface LabourStaffPerformanceSummary {
  totalHours: string;
  totalBreakHours: string;
  totalSales: number;
  totalSplh: number | null;
  totalTips: number;
  totalGratuity: number;
  totalVoids: number;
  totalVoidAmount: number;
}

export interface LabourStaffPerformanceResponse {
  content: LabourStaffPerformanceItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  summary: LabourStaffPerformanceSummary;
}

// GET /sales/live/v2/labour-working-hours-log
export interface LabourWorkingHoursLogItem {
  workDate: string;         // "YYYY-MM-DD"
  employeeName: string;
  clockIn: string;          // "HH:MM AM/PM" pre-formatted
  clockOut: string | null;  // "HH:MM AM/PM" pre-formatted
  status: string;
  workedHours: string;      // "Xh Ym" pre-formatted
  breakHours: string;       // "Xh Ym" pre-formatted
}

export interface LabourWorkingHoursLogResponse {
  content: LabourWorkingHoursLogItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// GET /sales/labour/employees
export interface LabourEmployee {
  staffId: string;
  staffName: string;
}

export interface LabourEmployeesResponse {
  employees: LabourEmployee[];
}

// GET /sales/live/v2/labour-kpi-comparison
//
// Every metric is nullable in practice: the endpoint returns `null` for a KPI it cannot
// compute for the window (no staff clocked in, no sales to divide by, …) rather than 0.
//
// `currentTime` / `locationTimezone` mirror the snapshot comparison endpoint — the
// server's own clock reading at the store when it computed these deltas. They feed the
// date control's "(to 7 AM)" truncation label on Labor (ZAR-1662), which is why Labor no
// longer depends on the browser clock for it.
export interface LabourKpiComparisonResponse {
  totalWorkingHours: string | null;
  staffWorked: number | null;
  employeeSales: number | null;
  splh: number | null;
  totalTips: number | null;
  voids: number | null;
  totalWorkingHoursDelta: number | null;
  staffWorkedDelta: number | null;
  employeeSalesDelta: number | null;
  splhDelta: number | null;
  totalTipsDelta: number | null;
  voidsDelta: number | null;
  /** Server clock at the store, "YYYY-MM-DD HH:mm:ss" in `locationTimezone`. */
  currentTime?: string;
  /** IANA zone the reading above is expressed in, e.g. "America/Chicago". */
  locationTimezone?: string;
}

/**
 * `channelGroup` — the sales channel an order belongs to. Reported on every
 * sales-by-channel-breakdown row and accepted as the `channelGroup` filter on
 * GET /sales/live/v2/order-log (this is what the breakdown drill-down sends —
 * NOT `channelName`, which filters by the POS order-type name instead).
 *
 * DIRECT_ONLINE / IN_STORE are first-party; every other value is a third-party
 * marketplace platform.
 */
export type OrderChannelGroup =
  | 'DIRECT_ONLINE'
  | 'IN_STORE'
  | 'UberEats'
  | 'Doordash'
  | 'GrubHub'
  | 'GloriaFood'
  | 'Swiggy'
  | 'Seamless'
  | 'Unknown';

/** On-screen name per channelGroup. Marketplace platforms read as their raw value. */
export const CHANNEL_GROUP_LABELS: Record<OrderChannelGroup, string> = {
  DIRECT_ONLINE: 'Direct online',
  IN_STORE: 'Instore',
  UberEats: 'UberEats',
  Doordash: 'Doordash',
  GrubHub: 'GrubHub',
  GloriaFood: 'GloriaFood',
  Swiggy: 'Swiggy',
  Seamless: 'Seamless',
  Unknown: 'Unknown',
};

/** Label for a channelGroup; unrecognised values fall back to the raw value. */
export const channelGroupLabel = (group: string): string =>
  CHANNEL_GROUP_LABELS[group as OrderChannelGroup] ?? group;

// GET /sales/live/v2/sales-by-channel-breakdown
// One row per (channelGroup, channel) pair. `channelGroup` is DIRECT_ONLINE / IN_STORE
// for first-party sales; any other value is a third-party marketplace platform.
export interface SalesByChannelBreakdownRow {
  channelGroup: string | null;
  channel: string | null;
  orderCount: number;
  itemTotal: number;
  discount: number;
  deliveryFee: number;
  convenienceFee: number;
  serviceFee: number;
  cardProcessingFee: number;
  tips: number;
  tax: number;
  /** Absent/null for outlets that do not capture a service charge — the column is then hidden. */
  serviceCharge?: number | null;
  totalCollected: number;
  /** REF-4 — order-level total refunded on this row's orders. Absent on older payloads. */
  refunded?: number | null;
}

export interface SalesByChannelBreakdownResponse {
  rows: SalesByChannelBreakdownRow[];
  // Grand-total row — `channelGroup` / `channel` are null.
  totals: SalesByChannelBreakdownRow | null;
}
